/**
 * Role Type Enum
 */
export enum RoleType {
  ADMIN_USER = 'admin_user',
  USER = 'user'
}

/**
 * Role Interface
 */
export interface IRole {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  isSystem: boolean;
  companyId?: number;
  roleType: RoleType;
  companyTypes?: string;      // Company types as comma-separated string: 'main,fleet'
  createdBy?: number;
  createdAt: Date;
  updatedAt: Date;
  company?: {
    id: number;
    name: string;
  };
}

/**
 * Role Model Class
 */
export class Role implements IRole {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  isSystem: boolean;
  companyId?: number;
  roleType: RoleType;
  companyTypes?: string;      // Company types as comma-separated string: 'main,fleet'
  createdBy?: number;
  createdAt: Date;
  updatedAt: Date;
  company?: {
    id: number;
    name: string;
  };

  constructor(data: IRole) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.isActive = data.isActive;
    this.isSystem = data.isSystem;
    this.companyId = data.companyId;
    this.roleType = data.roleType;
    this.companyTypes = data.companyTypes;
    this.createdBy = data.createdBy;
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
    this.company = data.company;
  }

  /**
   * Get display name with company
   */
  get displayName(): string {
    if (this.company) {
      return `${this.name} (${this.company.name})`;
    }
    return this.name;
  }

  /**
   * Get role type label
   */
  get typeLabel(): string {
    return this.roleType === RoleType.ADMIN_USER ? 'Admin User' : 'User';
  }

  /**
   * Get status label
   */
  get statusLabel(): string {
    return this.isActive ? 'Active' : 'Inactive';
  }

  /**
   * Check if role can be edited
   * System roles (companyId = null or isSystem = true) cannot be edited
   */
  get canEdit(): boolean {
    return !this.isSystem && this.companyId !== null && this.companyId !== undefined;
  }

  /**
   * Check if role can be deleted
   * System roles (companyId = null or isSystem = true) cannot be deleted
   */
  get canDelete(): boolean {
    return !this.isSystem && this.companyId !== null && this.companyId !== undefined;
  }

  /**
   * Get company types as array
   * Converts 'main,fleet' to ['main', 'fleet']
   */
  get companyTypesArray(): string[] {
    if (!this.companyTypes) {
      return [];
    }
    return this.companyTypes.split(',').map(type => type.trim()).filter(type => type.length > 0);
  }

  /**
   * Check if role is compatible with company type
   */
  isCompatibleWithCompanyType(companyType: string): boolean {
    if (!this.companyTypes) {
      // If no companyTypes specified, role is compatible with all company types
      return true;
    }
    const allowedTypes = this.companyTypesArray;
    return allowedTypes.includes(companyType);
  }
}
